import {
  ENUM_LABELS,
  type CreateReagentRecipeInput,
  type LabTest,
  type ReagentItemStock,
  type TestParameter,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { NumberField } from '#client/components/ui/NumberField';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { idChoice, nameAndCodeOptions } from '#client/lib/form-choices';
import type { RecipeLevel } from './recipe-form.logic';
import type { SetField } from '#client/hooks/useFormValues';

const LEVEL_OPTIONS: { value: RecipeLevel; label: string }[] = [
  { value: 'test', label: 'Once per sample' },
  { value: 'parameter', label: 'Once per analyte' },
];

interface FieldsProps {
  values: CreateReagentRecipeInput;
  onFieldChange: SetField<CreateReagentRecipeInput>;
  errors: Record<string, string>;
}

interface TargetCardProps extends FieldsProps {
  level: RecipeLevel;
  onLevelChange: (level: RecipeLevel) => void;
  tests: readonly LabTest[];
  parameters: readonly TestParameter[];
}

/** What consumes the reagent: a whole sample, or one analyte off it. */
export function RecipeTargetCard({
  values,
  onFieldChange,
  errors,
  level,
  onLevelChange,
  tests,
  parameters,
}: TargetCardProps) {
  return (
    <Card title="What consumes it" subtitle="A whole sample, or one analyte off that sample">
      <Segmented label="Level" options={LEVEL_OPTIONS} value={level} onChange={onLevelChange} />
      <p className="c-field__help">
        A shared diluent a CBC draws once belongs at sample level. The SOP&rsquo;s per-analyte
        volumes — &ldquo;ALP: 400 µL R1 + 100 µL R2&rdquo; — belong at analyte level. Where both
        exist for one reagent, the analyte line replaces the sample line rather than adding to it.
      </p>

      {level === 'test' ? (
        <Select
          label="Lab test"
          options={nameAndCodeOptions(tests)}
          placeholder="Choose a test"
          value={idChoice(values.test_id)}
          error={errors.test_id}
          onChange={(choice) => onFieldChange('test_id', Number(choice))}
          required
        />
      ) : (
        <Select
          label="Parameter"
          options={nameAndCodeOptions(parameters)}
          placeholder="Choose a parameter"
          value={idChoice(values.parameter_id)}
          error={errors.parameter_id}
          onChange={(choice) => onFieldChange('parameter_id', Number(choice))}
          required
        />
      )}
    </Card>
  );
}

interface QuantityCardProps extends FieldsProps {
  items: readonly ReagentItemStock[];
}

/** Which reagent, and how much of it one run draws. */
export function RecipeQuantityCard({ values, onFieldChange, errors, items }: QuantityCardProps) {
  const selectedItem = items.find((item) => item.id === values.item_id);
  const unit = selectedItem ? ENUM_LABELS.stockUnit[selectedItem.base_unit] : '';

  return (
    <Card title="Reagent and quantity">
      <div className="c-form-grid">
        <Select
          label="Reagent"
          options={nameAndCodeOptions(items)}
          placeholder="Choose a reagent"
          value={idChoice(values.item_id)}
          error={errors.item_id}
          onChange={(choice) => onFieldChange('item_id', Number(choice))}
          required
        />
        <NumberField
          label="Quantity per run"
          min={0.0001}
          step="any"
          value={values.quantity_base}
          error={errors.quantity_base}
          onChange={(quantity) => onFieldChange('quantity_base', quantity)}
          suffix={unit}
          helpText={
            selectedItem
              ? `In ${unit} — the unit this reagent is counted in`
              : 'Choose a reagent to see its unit'
          }
          required
        />
      </div>

      <TextArea
        label="From the SOP"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={2}
        helpText="The procedure line this came from. Documentation for the bench — the arithmetic ignores it."
      />

      <Checkbox
        label="Active"
        checked={values.is_active}
        onChange={(event) => onFieldChange('is_active', event.target.checked)}
      />
    </Card>
  );
}
